import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'
import type { LucideIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'
import toNumber from 'lodash/toNumber'

interface FormInputProps {
  label: string
  name: string
  type?: string
  icon?: LucideIcon
  inlayIcon?: LucideIcon
  inlayIconPosition?: 'left' | 'right'
  disabled?: boolean
  isEditing?: boolean
  className?: string
  placeholder?: string
  helperText?: string
  onBlur?: () => void
  max?: number
  min?: number
  step?: number | 'any'
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onNumberValueChange?: (value: number | undefined) => void
}

export default function FormInput({
  label,
  name,
  type = 'text',
  icon: Icon,
  inlayIcon,
  inlayIconPosition = 'left',
  disabled,
  isEditing = true,
  className,
  onBlur,
  helperText,
  placeholder,
  max,
  min,
  step,
  onChange,
  onNumberValueChange
}: FormInputProps) {
  const {
    register,
    clearErrors,
    formState: { errors }
  } = useFormContext()

  const fieldError = get(errors, name)
  const errorMessage = get(fieldError, 'message')

  if (type === 'number') {
    const {
      onChange: regOnChange,
      onBlur: regOnBlur,
      ref,
      name: regName
    } = register(name, {
      valueAsNumber: true,
      setValueAs: (value: string) => (value === '' ? undefined : toNumber(value))
    })

    const mergedOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e)
      } else if (onNumberValueChange) {
        void regOnChange(e)
        const raw = e.target.value
        onNumberValueChange(raw === '' ? undefined : toNumber(raw))
      } else {
        void regOnChange(e)
      }
      clearErrors(name)
    }

    return (
      <div className={cn('space-y-2', className)}>
        <Label htmlFor={name} className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </Label>
        <Input
          ref={ref}
          name={regName}
          id={name}
          type={type}
          disabled={disabled || !isEditing}
          inlayIcon={inlayIcon}
          inlayIconPosition={inlayIconPosition}
          onChange={mergedOnChange}
          onBlur={(e) => {
            void regOnBlur(e)
            onBlur?.()
          }}
          className={fieldError ? 'border-destructive' : ''}
          placeholder={placeholder}
          {...(max !== undefined ? { max } : {})}
          {...(min !== undefined ? { min } : {})}
          step={step ?? 'any'}
        />
        {errorMessage && <p className="text-sm text-destructive">{errorMessage.toString()}</p>}
        {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
      </div>
    )
  }

  const { onChange: regOnChange, onBlur: regOnBlur, ref, name: regName, ...registerRest } = register(name)

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={name} className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </Label>
      <Input
        ref={ref}
        name={regName}
        id={name}
        type={type}
        disabled={disabled || !isEditing}
        inlayIcon={inlayIcon}
        inlayIconPosition={inlayIconPosition}
        {...registerRest}
        onChange={(e) => {
          void regOnChange(e)
          onChange?.(e)
          clearErrors(name)
        }}
        onBlur={(e) => {
          void regOnBlur(e)
          onBlur?.()
        }}
        className={fieldError ? 'border-destructive' : ''}
        placeholder={placeholder}
        {...(max !== undefined ? { max } : {})}
        {...(min !== undefined ? { min } : {})}
        {...(step !== undefined ? { step } : {})}
      />
      {errorMessage && <p className="text-sm text-destructive">{errorMessage.toString()}</p>}
      {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}
